/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useCallback, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { AgenciesService } from '@services/requests/housien-services/AgenciesService';
import { AntiBotChallengeCancelledError } from '@shared/utils/antibot-challenge-stash';

export interface UsePaymentMethodSessionResult {
  formUrl: string | null;
  isLoading: boolean;
  isError: boolean;
  createSession: () => Promise<void>;
  reset: () => void;
}

/**
 * Creates a Payien payment-method-update session for an agency subscription.
 *
 * Calls POST /agencies/{agencyId}/subscription/payment-method-session and
 * exposes the resulting form_url for embedding in an iframe. Only one
 * session is ever in flight at a time — calling createSession() again while
 * isLoading is true is a no-op, so a double-click can't fire off two Payien
 * sessions.
 *
 * Each createSession() call mints one Idempotency-Key and hands it to the
 * request layer. Every retry the layer performs underneath — token refresh,
 * anti-bot challenge replay, generic HTTP retry — reuses the same options and
 * therefore the same key, so Payien replays the original session rather than
 * creating a second one. A genuinely new attempt (the user clicking again
 * after a failure, or changing the card twice) gets a fresh key, and so a
 * fresh session, which is what the in-flight guard alone can't distinguish.
 */
export function usePaymentMethodSession(agencyId: string): UsePaymentMethodSessionResult {
  const [formUrl, setFormUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const createSession = useCallback(async () => {
    if (isLoading) return;
    setIsLoading(true);
    setIsError(false);
    try {
      const result = await AgenciesService.createPaymentMethodSession(agencyId, uuidv4());
      setFormUrl(result.form_url);
    } catch (err) {
      // The user closing the challenge overlay isn't a failure worth an
      // error state -- they simply declined to complete verification.
      if (!(err instanceof AntiBotChallengeCancelledError)) {
        setIsError(true);
      }
    } finally {
      setIsLoading(false);
    }
  }, [agencyId, isLoading]);

  const reset = useCallback(() => {
    setFormUrl(null);
    setIsError(false);
  }, []);

  return { formUrl, isLoading, isError, createSession, reset };
}
